import { useEffect, useMemo, useRef, useState } from 'react';
import { Application } from '@core/Application';
import { RosterAnalyticsHelper } from '@core/Analytics/RosterAnalyticsHelper';
import { Organization } from '@models/Organization';
import { DashboardService } from '@services/DashboardService';
import { RosterService, RosterServiceModel } from '@services/RosterService';
import { QontyEmptyState } from '@components/QontyEmptyState';
import { QontyLoading } from '@components/QontyLoading';
import {
    HorizontalContainer,
    TableCell,
    TableHeaderCell,
    TableHeaderContainer,
    TableRowContainer,
    VerticalContainer,
} from '@shared/designSystem/Containers';
import { DateTriggerButton, RefreshButton } from '@shared/designSystem/Button';
import { Modal } from '@shared/designSystem/messages/Modal';
import ScrollArea from '@shared/designSystem/ScrollArea';
import SearchBar from '@shared/designSystem/SearchBar';
import StatCard from '@shared/designSystem/StatCard';
import DSStatusBadge from '@shared/designSystem/StatusBadge';
import UserCard from '@shared/designSystem/UserCard';
import { Text } from '@shared/designSystem/Texts';
import designSystemStyles from '@shared/styles/designSystemStyles';
import LocalDateSelector from './LocalDateSelector';
import {
    EnrichedRosterRow,
    formatCheckinTime,
    rosterStatusColor,
    rosterStatusLabel,
    useRosterScreenViewModel,
} from './RosterScreenViewModel';
import Utils from '@utils/Utils';

interface RosterScreenProps {
    org: Organization;
}

const gridTemplateColumns = '25fr 20fr 16fr 24fr 15fr';
const rowHeight = '72px';

const getRosterEmptyStateText = (
    hasRosterData: boolean,
    hasSearchQuery: boolean,
    hasStatusFilter: boolean,
): string => {
    if (!hasRosterData) {
        return 'No hay colaboradores planificados para este día.'.localized();
    }
    if (hasSearchQuery) {
        return 'No se encontraron colaboradores para esta búsqueda.'.localized();
    }
    if (hasStatusFilter) {
        return 'No hay colaboradores para el estado seleccionado.'.localized();
    }
    return 'No hay colaboradores para mostrar.'.localized();
};

interface StatusBadgeProps {
    status: RosterServiceModel.RosterStatus;
}

export const StatusBadge: React.FC<StatusBadgeProps> = ({ status }) => {
    const color = rosterStatusColor(status);
    const label = rosterStatusLabel(status);
    return <DSStatusBadge label={label} color={color} />;
};

interface CheckinCellProps {
    row: EnrichedRosterRow;
    timezone: string;
}

const CheckinCell: React.FC<CheckinCellProps> = ({ row, timezone }) => {
    if (row.actualCheckinTimestamp == null) {
        return <TableCell text="—" />;
    }

    const checkinTime = formatCheckinTime(row.actualCheckinTimestamp, timezone);
    const checkoutTime = row.checkOutTimestamp != null
        ? formatCheckinTime(row.checkOutTimestamp, timezone)
        : null;

    return (
        <VerticalContainer gap={designSystemStyles.gapNone} justifyContent="center">
            <Text text={`↑ ${checkinTime}`} />
            {checkoutTime != null
                ? <Text text={`↓ ${checkoutTime}`} />
                : <Text text={'Sin fichar salida'.localized()} color={designSystemStyles.colorTextSecondary} />
            }
        </VerticalContainer>
    );
};

const RosterScreen: React.FC<RosterScreenProps> = ({ org }) => {
    const rosterService = useMemo(
        () => new RosterService(Application.shared().coreDependencies.httpClient),
        [],
    );
    const dashboardService = useMemo(
        () => new DashboardService(Application.shared().coreDependencies.httpClient),
        [],
    );
    const [isDateSelectorOpen, setIsDateSelectorOpen] = useState(false);
    const hasTrackedShownRef = useRef(false);
    const previousSearchQueryRef = useRef('');

    const viewModel = useRosterScreenViewModel({ org, rosterService, dashboardService });

    const headerHeight = designSystemStyles.sizeRowMedium;
    const emptyStateText = getRosterEmptyStateText(
        viewModel.summary.total > 0,
        viewModel.searchQuery.trim().length > 0,
        viewModel.statusFilter != null,
    );

    useEffect(() => {
        if (hasTrackedShownRef.current) return;
        hasTrackedShownRef.current = true;
        RosterAnalyticsHelper.trackByCollaboratorShown(viewModel.day);
    }, [viewModel.day]);

    useEffect(() => {
        const query = viewModel.searchQuery.trim();
        if (previousSearchQueryRef.current === query) return;

        const timeoutID = window.setTimeout(() => {
            previousSearchQueryRef.current = query;
            RosterAnalyticsHelper.trackByCollaboratorSearchApplied({
                day: viewModel.day,
                queryLength: query.length,
                hasQuery: query.length > 0,
                resultCount: viewModel.filteredRows.length,
                totalCount: viewModel.summary.total,
                activeFilter: viewModel.statusFilter ?? 'all',
            });
        }, 600);

        return () => window.clearTimeout(timeoutID);
    }, [viewModel.day, viewModel.filteredRows.length, viewModel.searchQuery, viewModel.statusFilter, viewModel.summary.total]);

    const dayFromDate = (date: Date) => Utils.getDayStringFromTimestamp(date.getTime(), viewModel.timezone);

    const handleRefresh = () => {
        RosterAnalyticsHelper.trackByCollaboratorRefreshed(viewModel.day);
        void viewModel.refresh();
    };

    const handleStatusFilterSelected = (status: RosterServiceModel.RosterStatus | null) => {
        RosterAnalyticsHelper.trackByCollaboratorStatusFilterSelected(viewModel.day, status ?? 'all');
        viewModel.setStatusFilter(status);
    };

    const trackDateChangedIfNeeded = (day: string) => {
        if (day !== viewModel.day) {
            RosterAnalyticsHelper.trackByCollaboratorDateChanged(day);
        }
    };

    const handleDateSelected = (date: Date) => {
        trackDateChangedIfNeeded(dayFromDate(date));
        viewModel.setSelectedDate(date);
        setIsDateSelectorOpen(false);
    };

    const handleDateCleared = () => {
        const today = new Date();
        trackDateChangedIfNeeded(dayFromDate(today));
        viewModel.setSelectedDate(today);
        setIsDateSelectorOpen(false);
    };

    const tableContent = viewModel.loading ? (
        <QontyLoading text={'Cargando roster...'.localized()} withQonty={true} />
    ) : viewModel.filteredRows.length === 0 ? (
        <VerticalContainer width="100%" height="100%" alignment="center" justifyContent="center">
            <QontyEmptyState
                text={emptyStateText}
                darkMode={false}
            />
        </VerticalContainer>
    ) : (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, gap: designSystemStyles.gapMd, width: '100%' }}>
            <TableHeaderContainer height={headerHeight} gridTemplateColumns={gridTemplateColumns}>
                <TableHeaderCell text={'Colaborador'.localized()} />
                <TableHeaderCell text={'Qontact Point'.localized()} />
                <TableHeaderCell text={'Horario turno'.localized()} />
                <TableHeaderCell text={'Fichaje real'.localized()} />
                <TableHeaderCell text={'Estado'.localized()} />
            </TableHeaderContainer>
            <ScrollArea>
                {viewModel.filteredRows.map((row, index) => (
                    <TableRowContainer
                        key={index}
                        gridTemplateColumns={gridTemplateColumns} 
                        height={rowHeight} 
                        maxHeight={rowHeight} 
                        minHeight={rowHeight}
                        backgroundColor={index % 2 === 0 ? designSystemStyles.colorBackgroundWhite : designSystemStyles.colorBackgroundGray2}
                    >
                        <HorizontalContainer minWidth="0">
                            <UserCard name={row.userName} imageURL={row.userImage} width="100%" />
                        </HorizontalContainer>
                        <TableCell
                            text={row.buildingName || '—'}
                            truncate={true}
                        />
                        <TableCell
                            text={`${row.hourFrom} – ${row.hourTo}`}
                        />
                        <CheckinCell row={row} timezone={viewModel.timezone} />
                        <HorizontalContainer alignment="center">
                            <StatusBadge status={row.status} />
                        </HorizontalContainer>
                    </TableRowContainer>
                ))}
            </ScrollArea>
        </div>
    );

    return (
        <VerticalContainer
            width="100%"
            height="100%"
            minHeight="0"
            gap={designSystemStyles.gapLg}
        >
            <HorizontalContainer
                width="100%"
                gap={designSystemStyles.gapMd}
                alignment="center"
                justifyContent="space-between"
                flexWrap="wrap"
                flexShrink={0}
            >
                <HorizontalContainer
                    width="100%"
                    gap={designSystemStyles.gapMd}
                    alignment="center"
                    flexShrink={0}
                >
                    <SearchBar
                        value={viewModel.searchQuery}
                        onChange={viewModel.setSearchQuery}
                        placeholder={'Buscar colaborador...'.localized()}
                        width="1020px"
                    />
                    <RefreshButton onClick={handleRefresh} />
                    <HorizontalContainer 
                        alignment="flex-end" 
                        justifyContent="flex-end"
                        width="100%" 
                    >
                        <DateTriggerButton
                            text={viewModel.formattedDay}
                            onClick={() => setIsDateSelectorOpen(true)}
                        />
                    </HorizontalContainer>
                </HorizontalContainer>
                <HorizontalContainer
                    width="auto"
                    gap={designSystemStyles.gapMd}
                    alignment="center"
                    flexWrap="wrap"
                    minWidth="0"
                >
                    <StatCard label={'Total'.localized()} count={viewModel.summary.total} loading={viewModel.loading} color={designSystemStyles.colorTextSecondary} selected={viewModel.statusFilter === null} onClick={() => handleStatusFilterSelected(null)} />
                    <StatCard label={'Presentes'.localized()} count={viewModel.summary.presente} loading={viewModel.loading} color={rosterStatusColor('PRESENTE')} selected={viewModel.statusFilter === 'PRESENTE'} onClick={() => handleStatusFilterSelected(viewModel.statusFilter === 'PRESENTE' ? null : 'PRESENTE')} />
                    <StatCard label={'Tarde'.localized()} count={viewModel.summary.conDemora} loading={viewModel.loading} color={rosterStatusColor('CON_DEMORA')} selected={viewModel.statusFilter === 'CON_DEMORA'} onClick={() => handleStatusFilterSelected(viewModel.statusFilter === 'CON_DEMORA' ? null : 'CON_DEMORA')} />
                    <StatCard label={'Sin presentarse'.localized()} count={viewModel.summary.sinPresentarse} loading={viewModel.loading} color={rosterStatusColor('SIN_PRESENTARSE')} selected={viewModel.statusFilter === 'SIN_PRESENTARSE'} onClick={() => handleStatusFilterSelected(viewModel.statusFilter === 'SIN_PRESENTARSE' ? null : 'SIN_PRESENTARSE')} />
                    <StatCard label={'Ausentes'.localized()} count={viewModel.summary.ausente} loading={viewModel.loading} color={rosterStatusColor('AUSENTE')} selected={viewModel.statusFilter === 'AUSENTE'} onClick={() => handleStatusFilterSelected(viewModel.statusFilter === 'AUSENTE' ? null : 'AUSENTE')} />
                    <StatCard label={'Planificados'.localized()} count={viewModel.summary.planificado} loading={viewModel.loading} color={rosterStatusColor('PLANIFICADO')} selected={viewModel.statusFilter === 'PLANIFICADO'} onClick={() => handleStatusFilterSelected(viewModel.statusFilter === 'PLANIFICADO' ? null : 'PLANIFICADO')} />
                </HorizontalContainer>
            </HorizontalContainer>

            {/* Table */}
            {tableContent}

            {/* Date picker modal */}
            <Modal
                isOpen={isDateSelectorOpen}
                onClose={() => setIsDateSelectorOpen(false)}
                title={'Seleccionar fecha'.localized()}
                width="420px"
                maxWidth="92vw"
            >
                <LocalDateSelector
                    initialDate={viewModel.selectedDate}
                    events={{
                        onApply: (date) => {
                            handleDateSelected(date ?? new Date());
                        },
                        onClear: handleDateCleared,
                    }}
                />
            </Modal>
        </VerticalContainer>
    );
};

export default RosterScreen;
